
import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';
import RoleProtectedRoute from '@/components/RoleProtectedRoute';
import { useAuth } from '@/contexts/AuthContext';
import { LocationProvider, useLocation } from '@/contexts/LocationContext';
import { useDashboardStats } from '@/hooks/useDashboardStats';
import LocationSelector from '@/components/dashboard/LocationSelector';
import RevenueSnapshotWidget from '@/components/dashboard/widgets/RevenueSnapshotWidget';
import AppointmentStatusWidget from '@/components/dashboard/widgets/AppointmentStatusWidget';
import PatientActivityWidget from '@/components/dashboard/widgets/PatientActivityWidget';
import CreditTrackerWidget from '@/components/dashboard/widgets/CreditTrackerWidget';
import { DashboardWidget } from '@/types/dashboard';
import { Link } from 'react-router-dom';
import { 
  Calendar, 
  Users, 
  Plus, 
  CreditCard,
  Settings
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const EnhancedStaffDashboardContent = () => {
  const { user } = useAuth();
  const { currentLocation } = useLocation();
  const { stats, isLoading, error } = useDashboardStats(currentLocation?.id);
  
  const [widgets, setWidgets] = useState<DashboardWidget[]>([
    {
      id: 'revenue_snapshot',
      widget_key: 'revenue_snapshot',
      widget_name: 'Revenue Snapshot',
      default_enabled: true,
      required_role: ['admin', 'coordinator'],
      default_position: 0,
      isVisible: true,
      isCollapsed: false
    },
    {
      id: 'appointment_status',
      widget_key: 'appointment_status',
      widget_name: 'Appointment Status',
      default_enabled: true,
      required_role: ['admin', 'coordinator'],
      default_position: 1,
      isVisible: true,
      isCollapsed: false
    },
    {
      id: 'patient_activity',
      widget_key: 'patient_activity',
      widget_name: 'Patient Activity',
      default_enabled: true,
      required_role: ['admin', 'coordinator'],
      default_position: 2,
      isVisible: true,
      isCollapsed: false
    },
    {
      id: 'credit_tracker',
      widget_key: 'credit_tracker',
      widget_name: 'Credit Tracker',
      default_enabled: true,
      required_role: ['admin', 'coordinator'],
      default_position: 3,
      isVisible: true,
      isCollapsed: false
    }
  ]);

  const quickActions = [
    { title: 'Book Appointment', icon: Calendar, href: '/staff/appointments', color: '#A55C52' },
    { title: 'Patient Lookup', icon: Users, href: '/staff/patients', color: '#CDA79D' },
    { title: 'Add Credits', icon: Plus, href: '/staff/credits', color: '#5C322B' },
    { title: 'Process Payment', icon: CreditCard, href: '/staff/payments', color: '#A55C52' },
    { title: 'Manage Treatments', icon: Settings, href: '/staff/treatments', color: '#CDA79D' }
  ];

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(widgets);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setWidgets(items);
    // For now, skip saving to database since table doesn't exist yet
    // saveWidgetOrder(items);
  };

  // const saveWidgetOrder = async (widgetOrder: DashboardWidget[]) => {
  //   try {
  //     const { error } = await supabase
  //       .from('staff_preferences')
  //       .upsert({
  //         preference_type: 'widget_order',
  //         preference_value: { widgets: widgetOrder.map(w => w.id) }
  //       });

  //     if (error) throw error;
  //   } catch (error) {
  //     console.error('Error saving widget order:', error);
  //   }
  // };

  const toggleWidgetCollapse = (widgetId: string) => {
    setWidgets(widgets.map(widget => 
      widget.id === widgetId 
        ? { ...widget, isCollapsed: !widget.isCollapsed }
        : widget
    ));
  };

  const getCurrentDate = () => {
    return new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-juv-cream to-juv-sage/20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-juv-terracotta"></div>
          <p className="mt-4 text-juv-deep-chestnut">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-juv-cream to-juv-sage/20">
      {/* Enhanced Header */}
      <div className="bg-white shadow-sm border-b border-juv-rose-clay/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold font-juv-heading text-juv-deep-chestnut">
                Welcome back, {user?.email?.split('@')[0]} 👋
              </h1>
              <p className="mt-1 text-juv-deep-chestnut/70">
                Here's what's happening at your studio today.
              </p>
              <p className="text-sm text-gray-600">{getCurrentDate()}</p>
            </div>
            <div className="flex items-center space-x-4">
              <LocationSelector />
              <Link 
                to="/dashboard"
                className="text-sm hover:opacity-70 transition-colors text-juv-terracotta"
              >
                ← Back to Portal
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Quick Actions Grid - Always Fixed */}
          <div className="bg-white rounded-lg shadow-luxury border border-juv-rose-clay/30 p-6">
            <h2 className="text-xl font-juv-heading text-juv-deep-chestnut mb-4">⚙️ Quick Actions</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {quickActions.map((action, index) => {
                const Icon = action.icon;
                return (
                  <Button
                    key={index}
                    asChild
                    className="h-20 flex-col space-y-2 text-white hover:scale-105 transition-all duration-300"
                    style={{ backgroundColor: action.color }}
                  >
                    <Link to={action.href}>
                      <Icon className="h-5 w-5" />
                      <span className="text-sm font-medium text-center">{action.title}</span>
                    </Link>
                  </Button>
                );
              })}
            </div>
          </div>

          {/* Draggable Widgets */}
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="dashboard-widgets">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="grid grid-cols-1 lg:grid-cols-2 gap-6"
                >
                  {widgets.filter(w => w.isVisible).map((widget, index) => {
                    switch (widget.widget_key) {
                      case 'revenue_snapshot':
                        return (
                          <RevenueSnapshotWidget
                            key={widget.id}
                            id={widget.id}
                            index={index}
                            revenue={stats?.revenue || 0}
                            isCollapsed={widget.isCollapsed}
                            onToggleCollapse={() => toggleWidgetCollapse(widget.id)}
                          />
                        );
                      case 'appointment_status':
                        return (
                          <AppointmentStatusWidget
                            key={widget.id}
                            id={widget.id}
                            index={index}
                            appointmentsTotal={stats?.appointments_total || 0}
                            appointmentsCompleted={stats?.appointments_completed || 0}
                            appointmentsNoShow={stats?.appointments_no_show || 0}
                            isCollapsed={widget.isCollapsed}
                            onToggleCollapse={() => toggleWidgetCollapse(widget.id)}
                          />
                        );
                      case 'patient_activity':
                        return (
                          <PatientActivityWidget
                            key={widget.id}
                            id={widget.id}
                            index={index}
                            isCollapsed={widget.isCollapsed}
                            onToggleCollapse={() => toggleWidgetCollapse(widget.id)}
                          />
                        );
                      case 'credit_tracker':
                        return (
                          <CreditTrackerWidget
                            key={widget.id}
                            id={widget.id}
                            index={index}
                            creditsRedeemed={stats?.credits_redeemed || 0}
                            isCollapsed={widget.isCollapsed}
                            onToggleCollapse={() => toggleWidgetCollapse(widget.id)}
                          />
                        );
                      default:
                        return null;
                    }
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">Error loading dashboard data: {error}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const EnhancedStaffDashboard = () => {
  return (
    <RoleProtectedRoute allowedRoles={['admin', 'coordinator']}>
      <LocationProvider>
        <EnhancedStaffDashboardContent />
      </LocationProvider>
    </RoleProtectedRoute>
  );
};

export default EnhancedStaffDashboard;
